// components/modals/ClientEOICreateModal.jsx
import React from 'react';
import { Row, Col, Button, Alert, Badge } from 'react-bootstrap';
import {
    FaHandshake, FaCalendarAlt, FaUpload, FaTimes, FaImage,
    FaSpinner, FaCalculator, FaMapMarkerAlt, FaFileAlt, FaCloudUploadAlt
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import useClientEOICreate from './hooks/useClientEOICreate';

const ClientEOICreateModal = ({ show, onHide, professional, onSuccess }) => {
    const {
        formData,
        uploadedImages,
        isUploading,
        isSubmitting,
        isLoadingService,
        filteredSubServiceOptions,
        selectedSubService,
        calculatedAmount,
        isDragging,
        uploadErrors,
        fileInputRef,
        frequencyLabels,
        handleInputChange,
        handleSubServiceChange,
        handleImageUpload,
        removeImage,
        handleSubmit,
        handleClose,
        getDurationLabel,
        getDurationPlaceholder,
        handleDragEnter,
        handleDragLeave,
        handleDragOver,
        handleDrop
    } = useClientEOICreate(professional, onSuccess, onHide);

    if (!show) return null;

    // Composant Dropzone
    const Dropzone = ({ children, ...props }) => (
        <div
            {...props}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            style={{
                border: `2px dashed ${isDragging ? '#28a745' : '#dee2e6'}`,
                borderRadius: '12px',
                padding: '2rem',
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                backgroundColor: isDragging ? 'rgba(40, 167, 69, 0.05)' : '#f8f9fa',
                minHeight: '150px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative'
            }}
        >
            {children}
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageUpload}
                disabled={isUploading || uploadedImages.length >= 3 || isSubmitting}
                style={{
                    position: 'absolute',
                    inset: 0,
                    opacity: 0,
                    cursor: 'pointer',
                    width: '100%',
                    height: '100%'
                }}
            />
        </div>
    );

    // Gestion du champ durée : on retire automatiquement le "0" par défaut
    // dès que l'utilisateur commence à saisir une valeur.
    const handleQuantityChange = (e) => {
        let val = e.target.value;

        // Si la valeur contient plusieurs caractères et commence par un 0
        // (ex: "05"), on retire les zéros non significatifs.
        if (val.length > 1 && val.startsWith('0')) {
            val = val.replace(/^0+/, '');
        }

        handleInputChange('quantity', val);
    };

    const handleQuantityFocus = () => {
        // Si le champ affiche encore "0" au focus, on le vide pour laisser
        // l'utilisateur saisir directement sa valeur.
        if (formData.quantity === '0' || formData.quantity === 0) {
            handleInputChange('quantity', '');
        }
    };

    return (
        <>
            <div
                className="eoi-modal-overlay show"
                style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 1050, overflowY: 'auto'
                }}
                onClick={handleClose}
            >
                <div
                    className="eoi-modal-container"
                    style={{
                        position: 'absolute', bottom: 0, left: 0, right: 0,
                        backgroundColor: 'white', borderRadius: '24px 24px 0 0',
                        maxHeight: '95vh', overflowY: 'auto', animation: 'slideUp 0.4s ease'
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="modal-header-fixed" style={{
                        position: 'sticky', top: 0, left: 0, right: 0, zIndex: 100,
                        backgroundColor: 'white', borderBottom: '1px solid #e2e8f0',
                        borderTopLeftRadius: '24px', borderTopRightRadius: '24px', padding: '16px 24px'
                    }}>
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <h4 className="fw-bold mb-0 text-dark">
                                    <FaHandshake className="me-2 text-success" />
                                    Contacter {professional?.firstName} {professional?.lastName}
                                </h4>
                                <p className="text-muted small mt-1 mb-0">
                                    Envoyez une demande de service
                                </p>
                            </div>
                            <button className="btn-close" onClick={handleClose} disabled={isSubmitting}></button>
                        </div>
                    </div>

                    {/* Contenu */}
                    <div className="p-4">
                        {/* Section Service */}
                        <div className="bg-light rounded-3 p-3 mb-4">
                            <h5 className="fw-bold mb-3 text-success">🏠 Service demandé</h5>
                            <Row className="g-3">
                                <Col md={12}>
                                    <div className="display-field">
                                        <div className="field-label">
                                            Service du professionnel
                                        </div>
                                        <div className="field-value bg-white rounded p-2">
                                            {formData.serviceName || 'Non spécifié'}
                                        </div>
                                        <small className="text-muted">Service principal proposé par le professionnel</small>
                                    </div>
                                </Col>
                            </Row>
                        </div>

                        {/* Section Sous-service */}
                        <div className="bg-light rounded-3 p-3 mb-4">
                            <h5 className="fw-bold mb-3 text-success">📋 Compétences</h5>
                            {isLoadingService ? (
                                <div className="text-center py-4">
                                    <FaSpinner className="spinner fa-spin" size={24} />
                                    <span className="ms-2">Chargement des Compétences...</span>
                                </div>
                            ) : (
                                <div>
                                    {filteredSubServiceOptions.length > 0 ? (
                                        filteredSubServiceOptions.map((subService) => (
                                            <div
                                                key={subService.id}
                                                className={`subservice-option mb-2 p-3 border rounded-3 ${formData.subServiceName === subService.name ? 'border-success bg-success bg-opacity-10' : 'border-secondary'}`}
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => handleSubServiceChange(subService)}
                                            >
                                                <div className="d-flex flex-column">
                                                    <div className="d-flex align-items-center">
                                                        <input
                                                            type="radio"
                                                            id={`subService-${subService.id}`}
                                                            name="subService"
                                                            checked={formData.subServiceName === subService.name}
                                                            onChange={() => {}}
                                                            className="me-2 flex-shrink-0"
                                                            style={{ width: '18px', height: '18px' }}
                                                        />
                                                        <label htmlFor={`subService-${subService.id}`} className="mb-0 fw-medium" style={{ cursor: 'pointer' }}>
                                                            {subService.name}
                                                        </label>
                                                    </div>
                                                    {subService.amount && (
                                                        <div className="mt-2">
                                                            <Badge
                                                                bg="primary"
                                                                className="px-3 py-2 rounded-pill"
                                                                style={{
                                                                    whiteSpace: 'normal',
                                                                    wordBreak: 'break-word',
                                                                    textAlign: 'left',
                                                                    display: 'inline-block',
                                                                    maxWidth: '100%'
                                                                }}
                                                            >
                                                                {subService.amount.toLocaleString()} FCFA
                                                                {subService.frequency && ` / ${frequencyLabels[subService.frequency] || subService.frequency.toLowerCase()}`}
                                                            </Badge>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <Alert variant="warning" className="small">
                                            Aucune Compétence disponible pour ce professionnel.
                                        </Alert>
                                    )}
                                </div>
                            )}
                            <small className="text-muted d-block mt-2">
                                Sélectionnez la Compétence qui correspond à votre besoin
                            </small>
                        </div>

                        {/* Section Durée et Fréquence */}
                        {selectedSubService && (
                            <div className="bg-light rounded-3 p-3 mb-4">
                                <h5 className="fw-bold mb-3 text-success">⏱️ Détails du contrat</h5>
                                <Row className="g-3">
                                    <Col md={6}>
                                        <div className="display-field">
                                            <div className="field-label">
                                                {getDurationLabel()}
                                            </div>
                                            <input
                                                type="number"
                                                min="0"
                                                value={formData.quantity}
                                                onChange={handleQuantityChange}
                                                onFocus={handleQuantityFocus}
                                                placeholder={getDurationPlaceholder()}
                                                className={`form-control ${formData.quantity === '0' || formData.quantity === '' ? 'text-muted' : ''}`}
                                                disabled={isSubmitting}
                                            />
                                            <small className="text-muted">Durée totale du contrat (minimum: 1 mois)</small>
                                            {formData.quantity !== '' && parseInt(formData.quantity) < 1 && (
                                                <div className="text-danger small mt-1">
                                                    La durée doit être égale ou supérieure à 1
                                                </div>
                                            )}
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="display-field">
                                            <div className="field-label">
                                                Fréquence
                                            </div>
                                            <div className="field-value bg-white rounded p-2">
                                                {frequencyLabels[formData.frequency] || formData.frequency || 'Non définie'}
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            </div>
                        )}

                        {/* Budget estimé */}
                        {calculatedAmount > 0 && (
                            <Alert variant="success" className="mb-4 rounded-3">
                                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                    <div>
                                        <FaCalculator className="me-2" />
                                        <strong>Budget estimé:</strong>
                                    </div>
                                    <div>
                                        <span className="h5 text-success fw-bold mb-0">
                                            {calculatedAmount.toLocaleString()} FCFA
                                        </span>
                                    </div>
                                </div>
                                {selectedSubService && (
                                    <small className="text-muted d-block mt-2">
                                        {selectedSubService.amount.toLocaleString()} FCFA × {formData.quantity} {getDurationLabel().replace('Durée du contrat ', '').toLowerCase()} = {calculatedAmount.toLocaleString()} FCFA
                                    </small>
                                )}
                            </Alert>
                        )}

                        {/* Localisation et Date */}
                        <div className="bg-light rounded-3 p-3 mb-4">
                            <h5 className="fw-bold mb-3 text-success">📍 Informations de service</h5>
                            <Row className="g-3">
                                <Col md={6}>
                                    <div className="display-field">
                                        <div className="field-label">
                                            <FaMapMarkerAlt className="me-1" /> Lieu d'intervention *
                                        </div>
                                        <input
                                            type="text"
                                            placeholder="Adresse du service"
                                            value={formData.serviceLocation}
                                            onChange={(e) => handleInputChange('serviceLocation', e.target.value)}
                                            className="form-control"
                                            disabled={isSubmitting}
                                        />
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="display-field">
                                        <div className="field-label">
                                            <FaCalendarAlt className="me-1" /> Date de début souhaitée *
                                        </div>
                                        <input
                                            type="date"
                                            value={formData.desiredStartDate}
                                            onChange={(e) => handleInputChange('desiredStartDate', e.target.value)}
                                            min={new Date().toISOString().split('T')[0]}
                                            className="form-control"
                                            disabled={isSubmitting}
                                        />
                                    </div>
                                </Col>
                            </Row>
                        </div>

                        {/* Description */}
                        <div className="bg-light rounded-3 p-3 mb-4">
                            <h5 className="fw-bold mb-3 text-success">📝 Description du projet</h5>
                            <div className="display-field">
                                <textarea
                                    rows={4}
                                    placeholder="Décrivez votre projet en détail..."
                                    value={formData.comments}
                                    onChange={(e) => handleInputChange('comments', e.target.value)}
                                    className="form-control"
                                    disabled={isSubmitting}
                                />
                                <small className="text-muted">* Champs obligatoire</small>
                            </div>
                        </div>

                        {/* Section Upload d'images - Style Dropzone */}
                        <div className="bg-light rounded-3 p-3 mb-4">
                            <h5 className="fw-bold mb-3 text-success">
                                <FaImage className="me-2" />
                                Photos du projet
                            </h5>
                            <p className="text-muted small mb-3">
                                Optionnel - max 3 images, 10MB par image
                            </p>

                            {uploadedImages.length > 0 && (
                                <div className="mb-3">
                                    <Row className="g-2">
                                        {uploadedImages.map((image, index) => (
                                            <Col xs={4} key={index}>
                                                <div className="position-relative">
                                                    <img
                                                        src={image.preview}
                                                        alt={`Upload ${index + 1}`}
                                                        className="img-thumbnail rounded-3"
                                                        style={{ width: '100%', height: '100px', objectFit: 'cover' }}
                                                    />
                                                    <button
                                                        className="btn btn-danger btn-sm position-absolute top-0 end-0 rounded-circle"
                                                        style={{ transform: 'translate(30%, -30%)', width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                                        onClick={() => removeImage(index)}
                                                        disabled={isSubmitting}
                                                    >
                                                        <FaTimes size={12} />
                                                    </button>
                                                </div>
                                            </Col>
                                        ))}
                                    </Row>
                                    <small className="text-muted d-block mt-2">
                                        {uploadedImages.length}/3 images uploadées
                                    </small>
                                </div>
                            )}

                            {uploadedImages.length < 3 && (
                                <Dropzone>
                                    <FaCloudUploadAlt
                                        size={48}
                                        color={isDragging ? '#28a745' : '#6c757d'}
                                        style={{ marginBottom: '1rem', transition: 'color 0.3s ease' }}
                                    />
                                    <p className="mb-0 fw-medium" style={{ color: isDragging ? '#28a745' : '#495057' }}>
                                        {isDragging ? 'Déposez vos images ici' : 'Glissez-déposez vos images ici'}
                                    </p>
                                    <p className="text-muted small mb-0">
                                        ou cliquez pour parcourir
                                    </p>
                                    <div className="mt-2">
                                        <Badge bg="light" text="dark" className="me-1">JPG</Badge>
                                        <Badge bg="light" text="dark" className="me-1">PNG</Badge>
                                        <Badge bg="light" text="dark" className="me-1">GIF</Badge>
                                        <Badge bg="light" text="dark">WEBP</Badge>
                                    </div>
                                    {isUploading && (
                                        <div className="mt-2">
                                            <FaSpinner className="fa-spin me-2" />
                                            <span className="small">Upload en cours...</span>
                                        </div>
                                    )}
                                    {uploadErrors.length > 0 && (
                                        <div className="mt-2 text-danger small">
                                            {uploadErrors.map((err, i) => (
                                                <div key={i}>• {err}</div>
                                            ))}
                                        </div>
                                    )}
                                </Dropzone>
                            )}

                            <div className="mt-2">
                                <small className="text-muted">
                                    {uploadedImages.length}/3 images. Formats acceptés: JPG, PNG, GIF, WEBP. Max 10MB par image.
                                </small>
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="modal-footer-fixed" style={{
                        position: 'sticky', bottom: 0, left: 0, right: 0,
                        backgroundColor: 'white', borderTop: '1px solid #e2e8f0',
                        padding: '16px 24px', marginTop: 'auto'
                    }}>
                        <div className="d-flex justify-content-end gap-3">

                            <button
                                onClick={handleSubmit}
                                className="btn btn-success rounded-pill px-4"
                                disabled={
                                    !formData.serviceId ||
                                    !formData.serviceLocation ||
                                    !formData.desiredStartDate ||
                                    !formData.comments ||
                                    !formData.subServiceName ||
                                    !formData.serviceFee ||
                                    calculatedAmount === 0 ||
                                    isSubmitting ||
                                    !formData.quantity ||
                                    formData.quantity === '' ||
                                    parseInt(formData.quantity) < 1
                                }
                            >
                                <FaHandshake className="me-2" />
                                {isSubmitting ? 'Envoi en cours...' : 'Envoyer la demande'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <style jsx>{`
                @keyframes slideUp {
                    from { transform: translateY(100%); }
                    to { transform: translateY(0); }
                }

                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }

                .eoi-modal-overlay {
                    animation: fadeIn 0.3s ease;
                }

                .modal-header-fixed, .modal-footer-fixed {
                    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
                }

                ::-webkit-scrollbar {
                    width: 6px;
                }

                ::-webkit-scrollbar-track {
                    background: #f1f1f1;
                }

                ::-webkit-scrollbar-thumb {
                    background: #888;
                    border-radius: 3px;
                }

                ::-webkit-scrollbar-thumb:hover {
                    background: #555;
                }

                .field-label {
                    font-size: 0.85rem;
                    font-weight: 600;
                    color: #2c3e50;
                    margin-bottom: 0.5rem;
                }

                .field-value {
                    font-size: 0.95rem;
                    color: #1a202c;
                }

                .subservice-option {
                    transition: all 0.2s ease;
                    cursor: pointer;
                }

                .subservice-option:hover {
                    transform: translateX(4px);
                    border-color: #28a745 !important;
                }

                @keyframes fa-spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }

                .fa-spin {
                    animation: fa-spin 1s linear infinite;
                }
            `}</style>
        </>
    );
};

export default ClientEOICreateModal;